import React from "react"
import styled, { css } from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import { Cart, Menu, Search } from "@components/icons"
import Logo from "@components/Logo"
import CountBubble from "@components/CountBubble"
import CountdownTimer from "@components/CountdownTimer"
import DesktopPromoBanner from "@components/PromoBanner/DesktopPromoBanner"
import { PromoProps } from "@components/PromoBanner/common"
import DefaultHeaderContent from "./DefaultHeaderContent"
import useScrollHandler from "@hooks/useScrollHandler"

const Background = styled.div<{
  shorten?: boolean
  hasCountdownTimer?: boolean
  showPromoBanner?: boolean
  collapsePromoBanner?: boolean
}>`
  ${({
    shorten,
    hasCountdownTimer,
    showPromoBanner,
    collapsePromoBanner,
  }) => css`
    z-index: 999;
    position: fixed;
    top: ${hasCountdownTimer && showPromoBanner && !collapsePromoBanner
      ? "92px"
      : showPromoBanner && !collapsePromoBanner
      ? "36px"
      : hasCountdownTimer
      ? "56px"
      : 0};
    will-change: transform;
    transform-origin: top;
    transform: ${shorten
      ? "scaleY(calc(64 / 88)) translateZ(0)"
      : "scaleY(1) translateZ(0)"};
    transition: transform ${animation.quick} ${cubicBezier};
    width: 100%;
    height: 88px;
    background-color: ${colors.offWhite};
  `}
`

const Container = styled.div<{
  shorten?: boolean
  hasCountdownTimer?: boolean
  showPromoBanner?: boolean
  collapsePromoBanner?: boolean
}>`
  ${({
    shorten,
    hasCountdownTimer,
    showPromoBanner,
    collapsePromoBanner,
  }) => css`
    z-index: 1000;
    position: fixed;
    top: ${hasCountdownTimer && showPromoBanner && !collapsePromoBanner
      ? "92px"
      : showPromoBanner && !collapsePromoBanner
      ? "36px"
      : hasCountdownTimer
      ? "56px"
      : 0};
    flex-direction: row;
    align-items: center;
    display: flex;
    transform-origin: top;
    transition: height ${animation.quick} ${cubicBezier};
    will-change: height;
    width: 100%;
    height: ${shorten ? "64px" : "88px"};
    padding-left: 40px;
    padding-right: 20px;
    border-bottom: ${shorten ? `solid 1px ${colors.dividerLineGray}` : "none"};
  `}
`

const CountdownTimerWrapper = styled.div`
  z-index: 1002;
  position: fixed;
  top: 0;
  transition: height ${animation.quick} ${cubicBezier};
  width: 100%;
  height: 56px;
`

const PromoBannerWrapper = styled.div<{
  hasCountdownTimer: boolean
  collapse: boolean
}>`
  ${({ hasCountdownTimer, collapse }) => css`
    z-index: 1001;
    position: fixed;
    top: ${hasCountdownTimer ? "56px" : 0};
    overflow: ${collapse ? "hidden" : "visibile"};
    transition: height ${animation.quick} ${cubicBezier};
    width: 100%;
    height: ${collapse ? 0 : 36}px;
  `}
`

const Content = styled.div`
  flex-direction: row;
  flex-grow: 1;
  align-items: center;
  justify-content: flex-end;
  display: flex;
  overflow: hidden;
  height: 100%;
  margin-left: 32px;
`

const defaultHeaderProps = `
  display: flex;
  height: 100%;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
`

const Header = styled.div`
  ${defaultHeaderProps}
`

const Headers = styled.div`
  height: 100%;
  min-width: 0;
  flex-grow: 1;
`

const hideAnimatedHeader = `
  opacity: 0;
  transition: opacity ${animation.quick} ${cubicBezier};
  pointer-events: none;
`
const AnimatedHeader = styled.div<{ hide?: boolean }>`
  ${defaultHeaderProps}
  opacity: 1;
  will-change: opacity;
  transition: opacity ${animation.medium} ${cubicBezier};
  ${({ hide }) => (hide ? hideAnimatedHeader : "")}
`

const showFixedHeaderProps = `
  opacity: 1;
  transform: translateY(calc(-50% - 32px)) translateZ(0);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  z-index: 1;
`
const showFixedHeaderWithCountdownTimerProps = `
  opacity: 1;
  transform: translateY(calc(-50% - 88px)) translateZ(0);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.medium} ${cubicBezier};
  z-index: 1;
`
const FixedHeader = styled.div<{ hasCountdownTimer?: boolean; show?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  will-change: opacity, transform;
  opacity: 0;
  transform: translateY(-50%) translateZ(0);
  transition: opacity ${animation.medium} ${cubicBezier},
    transform ${animation.quick} ${cubicBezier};
  pointer-events: none;
  z-index: -1;
  ${({ hasCountdownTimer, show }) =>
    show
      ? hasCountdownTimer
        ? showFixedHeaderWithCountdownTimerProps
        : showFixedHeaderProps
      : ""}
`

const Button = styled.button`
  display: flex;
  height: 100%;
  padding: 0 12px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  will-change: background-color;
  transition: background-color ${animation.medium} ${cubicBezier};
  background: none;
  border: none;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  @media ${breakpoints.desktop} {
    padding: 4px 20px 0;
  }

  @media ${breakpoints.largeDesktop} {
    width: 88px;
    padding: 0;

    &:hover {
      background-color: ${colors.dividerLineGray};
    }
  }
`

const SearchButton = styled(Button)``

const CartButton = styled(Button)`
  @media ${breakpoints.largeDesktop} {
    margin-left: 8px;
  }
`

const MenuButton = styled(Button)``

const Icon = styled.div`
  width: 24px;
  height: 24px;
`

export interface DesktopHeaderProps {
  enterpriseLink: string
  supportLink: string
  onClickSearch?: (e) => void
  onClickCart?: (e) => void
  onClickMenu?: (e) => void
  onClickExplore?: (e) => void
  searchValue?: string
  megaMenuHeaderRef?: any
  megaMenuHeaderItems?: Array<{
    label: string
    onClick?: () => void
  }>
  megaMenuActiveItem?: string
  productCount?: number
  expanded?: boolean
  filters?:
    | {
        deviceType?: {
          slug: string
          name: string
          menuLabel?: string
        }
        brand?: {
          slug: string
          name: string
        }
        device?: {
          slug: string
          name: string
        }
        series?: {
          name: string
        }
      }
    | {}
  product?: object
  countdownTimer?: {
    eventStart: string
    eventEnd?: string
    textBeforeEvent: string
    textDuringEvent?: string
    textAfterEvent?: string
  }
  promos?: Array<PromoProps>
  promoScrollDelay: string
  collapsePromoBanner?: boolean
  showPromoBanner?: boolean
  showFilters?: boolean
  showFixedHeader?: boolean
  fixedHeaderTopRef?: any
  FixedHeaderComponent?: React.ReactNode
  shortenDesktopHeader?: boolean
  showRewardsPoints?: boolean
  rewardsPoints?: number
  isLoggedIn?: boolean
  onLogout?: () => void
}

const DesktopHeader = (props: DesktopHeaderProps) => {
  const { showFixedHeader, fixedHeaderTopRef, FixedHeaderComponent } = props
  const hasCountdownTimer = !!props.countdownTimer

  const [refPos, setRefPos] = React.useState<any>(0)
  const handleShowFixedHeader = () => {
    if (fixedHeaderTopRef && fixedHeaderTopRef.current) {
      setRefPos(fixedHeaderTopRef.current.getBoundingClientRect().top)
    }
  }

  if (fixedHeaderTopRef) {
    useScrollHandler({ onScroll: handleShowFixedHeader })
  }

  const shouldShowFixedHeader =
    fixedHeaderTopRef &&
    refPos &&
    refPos < (hasCountdownTimer ? -116 : -60) &&
    !!showFixedHeader
  return (
    <>
      {props.countdownTimer && (
        <CountdownTimerWrapper>
          <CountdownTimer
            eventStart={props.countdownTimer.eventStart}
            eventEnd={props.countdownTimer.eventEnd}
            textBeforeEvent={props.countdownTimer.textBeforeEvent}
            textDuringEvent={props.countdownTimer.textDuringEvent}
            textAfterEvent={props.countdownTimer.textAfterEvent}
          />
        </CountdownTimerWrapper>
      )}
      {props.showPromoBanner && props.promos && (
        <PromoBannerWrapper
          hasCountdownTimer={hasCountdownTimer}
          collapse={!!props.collapsePromoBanner}
        >
          <DesktopPromoBanner
            enterpriseLink={props.enterpriseLink}
            supportLink={props.supportLink}
            promos={props.promos}
            promoScrollDelay={props.promoScrollDelay}
            showRewardsPoints={props.showRewardsPoints}
            rewardsPoints={props.rewardsPoints}
            isLoggedIn={props.isLoggedIn}
            onLogout={props.onLogout}
          />
        </PromoBannerWrapper>
      )}
      <Background
        shorten={props.shortenDesktopHeader}
        hasCountdownTimer={hasCountdownTimer}
        showPromoBanner={props.showPromoBanner}
        collapsePromoBanner={props.collapsePromoBanner}
      />
      <Container
        id="desktopHeader"
        shorten={props.shortenDesktopHeader}
        hasCountdownTimer={hasCountdownTimer}
        showPromoBanner={props.showPromoBanner}
        collapsePromoBanner={props.collapsePromoBanner}
      >
        <Logo />
        <Content>
          {showFixedHeader ? (
            <Headers>
              <AnimatedHeader hide={shouldShowFixedHeader}>
                <DefaultHeaderContent {...props} />
              </AnimatedHeader>
              <FixedHeader
                hasCountdownTimer={hasCountdownTimer}
                show={shouldShowFixedHeader}
              >
                {FixedHeaderComponent}
              </FixedHeader>
            </Headers>
          ) : (
            <Header>
              <DefaultHeaderContent {...props} />
            </Header>
          )}
          <SearchButton onClick={props.onClickSearch} aria-label="Search">
            <Icon>
              <Search />
            </Icon>
          </SearchButton>
          <CartButton onClick={props.onClickCart} aria-label="Cart">
            <CountBubble count={props.productCount || 0} />
            <Icon>
              <Cart />
            </Icon>
          </CartButton>
          <MenuButton onClick={props.onClickMenu} aria-label="Menu">
            <Icon>
              <Menu />
            </Icon>
          </MenuButton>
        </Content>
      </Container>
    </>
  )
}

export default DesktopHeader
